// src/context/AuthContext.tsx - Updated for real API
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiService, { ApiError } from '../services/api';
import { User, LoginCredentials, RegisterData } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (email: string, username: string, password: string, displayName?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          console.log('Found token, fetching user data...');
          const userData = await apiService.getCurrentUser();
          console.log('User data fetched:', userData);
          setUser(userData);
        } catch (error) {
          console.error('Failed to get current user:', error);
          localStorage.removeItem('token');
          setError('Session expired. Please log in again.');
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Attempting login for:', email);
      
      const credentials: LoginCredentials = { email, password };
      const response = await apiService.login(credentials);
      
      console.log('Login successful:', response);
      
      localStorage.setItem('token', response.token);
      setUser(response.user);
      
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      const errorMessage = error instanceof ApiError ? error.message : 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, username: string, password: string, displayName?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Attempting registration for:', email, username);
      
      const registerData: RegisterData = { 
        email, 
        username, 
        password,
        display_name: displayName 
      };
      const response = await apiService.register(registerData);
      
      console.log('Registration successful:', response);
      
      localStorage.setItem('token', response.token);
      setUser(response.user);
      
      return { success: true };
    } catch (error) {
      console.error('Registration failed:', error);
      const errorMessage = error instanceof ApiError ? error.message : 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    console.log('Logging out user');
    localStorage.removeItem('token');
    setUser(null);
    setError(null);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    try {
      console.log('Refreshing user data...');
      const userData = await apiService.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};